import * as React from 'react';
import { compose, withHandlers, withProps, withState } from 'recompose';
import { TranslateProps } from 'util/i18n';
import { translate } from 'react-i18next';
import { Input } from 'ui/shared/Input';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { HourOptions } from 'ui/pages/designer/sections/faceRec/HourOptions';
import { Required } from 'ui/pages/designer/Required';

import './FaceRecognitionSection.css';

const MIN_CONFIDENCE = 0;
const MAX_CONFIDENCE = 100;

const MIN_BLOCKSIZE = 10;
const MAX_BLOCKSIZE = 240;
const STEP_BLOCKSIZE = 5;

const MIN_BLOCKSBEYOND = 0;
const MAX_BLOCKSBEYOND = 5;

const MIN_TIMEOUT1STMATCH = 0;
const MAX_TIMEOUT1STMATCH = 5;

const requiredStyle = {
    verticalAlign: 'top',
};

type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const RawFaceRecognitionSection = ({
    reportSettings,
    onChange,
    filterReverseMatches,
    confidenceThreshold,
    blockSizeMinutes,
    blocksBeyondCurrent,
    timeoutForFirstMatch,
    onChangeFilterReverseMatches,
    onChangeConfidenceThreshold,
    onChangeBlockSizeMinutes,
    onChangeBlocksBeyondCurrent,
    onChangeTimeoutForFirstMatch,
    onBlurConfidenceThreshold,
    onBlurBlockSizeMinutes,
    onBlurBlocksBeyondCurrent,
    onBlurTimeoutForFirstMatch,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <div id="edit-report-face-rec">
        <label
            htmlFor="edit-report-face-rec-reverse"
        >
            { t('Reports_FaceRec_RemoveReverseMatches') }
        </label>
        <input
            type="checkbox"
            id="edit-report-face-rec-reverse"
            checked={ filterReverseMatches }
            onChange={ onChangeFilterReverseMatches }
        />
        <span/>

        <label
            htmlFor="edit-report-face-rec-confidence"
        >
            { t('Reports_FaceRec_ConfThresh') }
        </label>
        <Input
            type="number"
            id="edit-report-face-rec-confidence"
            min={ MIN_CONFIDENCE }
            max={ MAX_CONFIDENCE }
            value={ confidenceThreshold }
            onChange={ onChangeConfidenceThreshold }
            onBlur={ onBlurConfidenceThreshold }
        />
        <Input
            type="range"
            id="edit-report-face-rec-confidence-range"
            min={ MIN_CONFIDENCE }
            max={ MAX_CONFIDENCE }
            value={ confidenceThreshold }
            onChange={ onChangeConfidenceThreshold }
        />

        <label
            htmlFor="edit-report-face-rec-blocksize"
        >
            { t('Reports_FaceRec_ReportingBlockSize') }
        </label>
        <Input
            type="number"
            id="edit-report-face-rec-blocksize"
            min={ MIN_BLOCKSIZE }
            max={ MAX_BLOCKSIZE }
            step={ STEP_BLOCKSIZE }
            value={ blockSizeMinutes }
            onChange={ onChangeBlockSizeMinutes }
            onBlur={ onBlurBlockSizeMinutes }
        />
        <Input
            type="range"
            id="edit-report-face-rec-blocksize-range"
            min={ MIN_BLOCKSIZE }
            max={ MAX_BLOCKSIZE }
            step={ STEP_BLOCKSIZE }
            value={ blockSizeMinutes }
            onChange={ onChangeBlockSizeMinutes }
        />

        <label
            htmlFor="edit-report-face-rec-blocksbeyond"
        >
            { t('Reports_FaceRec_BlocksBeyondCurrent') }
        </label>
        <Input
            type="number"
            id="edit-report-face-rec-blocksbeyond"
            min={ MIN_BLOCKSBEYOND }
            max={ MAX_BLOCKSBEYOND }
            value={ blocksBeyondCurrent }
            onChange={ onChangeBlocksBeyondCurrent }
            onBlur={ onBlurBlocksBeyondCurrent }
        />
        <Input
            type="range"
            id="edit-report-face-rec-blocksbeyond-range"
            min={ MIN_BLOCKSBEYOND }
            max={ MAX_BLOCKSBEYOND }
            value={ blocksBeyondCurrent }
            onChange={ onChangeBlocksBeyondCurrent }
        />

        <label
            htmlFor="edit-report-face-rec-timeout"
        >
            { t('Reports_FaceRec_TimeoutForFirstMatch') }
        </label>
        <Input
            type="number"
            id="edit-report-face-rec-timeout"
            min={ MIN_TIMEOUT1STMATCH }
            max={ MAX_TIMEOUT1STMATCH }
            value={ timeoutForFirstMatch }
            onChange={ onChangeTimeoutForFirstMatch }
            onBlur={ onBlurTimeoutForFirstMatch }
        />
        <Input
            type="range"
            id="edit-report-face-rec-timeout-range"
            min={ MIN_TIMEOUT1STMATCH }
            max={ MAX_TIMEOUT1STMATCH }
            value={ timeoutForFirstMatch }
            onChange={ onChangeTimeoutForFirstMatch }
        />

        <label
            htmlFor="edit-report-face-rec-hours"
        >
            { t('Reports_FaceRec_HourOptions') }
        </label>
        <div className="inline-child-divs grid2span2">
            <HourOptions
                reportSettings={ reportSettings }
                onChange={ onChange }
            />
            <div style={ requiredStyle }>
                <Required visible={ !reportSettings.hourOptions }/>
            </div>
        </div>
    </div>
;

type RecomposeProps = {
    filterReverseMatches: boolean;
    confidenceThreshold: string;
    blockSizeMinutes: string;
    blocksBeyondCurrent: string;
    timeoutForFirstMatch: string;
    onChangeFilterReverseMatches: React.ChangeEventHandler<HTMLInputElement>;
    onChangeConfidenceThreshold: React.ChangeEventHandler<HTMLInputElement>;
    onChangeBlockSizeMinutes: React.ChangeEventHandler<HTMLInputElement>;
    onChangeBlocksBeyondCurrent: React.ChangeEventHandler<HTMLInputElement>;
    onChangeTimeoutForFirstMatch: React.ChangeEventHandler<HTMLInputElement>;
    onBlurConfidenceThreshold: React.FocusEventHandler<HTMLInputElement>;
    onBlurBlockSizeMinutes: React.FocusEventHandler<HTMLInputElement>;
    onBlurBlocksBeyondCurrent: React.FocusEventHandler<HTMLInputElement>;
    onBlurTimeoutForFirstMatch: React.FocusEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setConfidenceThreshold: (s: string) => void;
    _setBlockSizeMinutes: (s: string) => void;
    _setBlocksBeyondCurrent: (s: string) => void;
    _setTimeoutForFirstMatch: (s: string) => void;
};

// Helper method to extract the given property value out of ReportSettings
const readSetting = (propName: keyof ReportSettings) => ({ reportSettings }: OwnProps): string =>
    reportSettings[propName]!.toString()
;

// Helper method to update a value in the report settings.
const setSetting = (setterName: keyof ReportSettings, minValue: number, maxValue: number, enforceBounds: boolean) =>
    (props: OwnProps & _RecomposeProps) => (e: React.SyntheticEvent<HTMLInputElement>) => {
        // Read the value from the <input> element
        const valueString = e.currentTarget.value;
        const value = Math.max(minValue, Math.min(parseInt(valueString, 10), maxValue));

        // Update our internal state with the new value
        const _internalSetter = props[`_${setterName}`];
        if (enforceBounds) {
            _internalSetter(value.toString());
        }
        else {
            _internalSetter(valueString);
        }

        if (!isNaN(value)) {
            // Update the value in the report
            props.onChange(reportSettings => (reportSettings[setterName] as any)(value)); // tslint:disable-line:no-any
        }
    }
;

export const FaceRecognitionSection = compose<RecomposeProps & TranslateProps, OwnProps>(
    translate(),
    withProps<Partial<RecomposeProps>, OwnProps>(({ reportSettings }) => ({
        filterReverseMatches: reportSettings.filterReverseMatches,
    })),
    withState('confidenceThreshold', '_setConfidenceThreshold',   readSetting('confidenceThreshold')),
    withState('blockSizeMinutes',    '_setBlockSizeMinutes',      readSetting('blockSizeMinutes')),
    withState('blocksBeyondCurrent', '_setBlocksBeyondCurrent',   readSetting('blocksBeyondCurrent')),
    withState('timeoutForFirstMatch', '_setTimeoutForFirstMatch', readSetting('timeoutForFirstMatch')),
    withHandlers<OwnProps & _RecomposeProps, Partial<RecomposeProps>>({
        onChangeFilterReverseMatches: ({ onChange }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const enabled = e.currentTarget.checked;
            onChange(reportSettings => reportSettings.setFilterReverseMatches(enabled));
        },

        onChangeConfidenceThreshold:  setSetting('setConfidenceThreshold',  MIN_CONFIDENCE,      MAX_CONFIDENCE,      false),
        onChangeBlockSizeMinutes:     setSetting('setBlockSizeMinutes',     MIN_BLOCKSIZE,       MAX_BLOCKSIZE,       false),
        onChangeBlocksBeyondCurrent:  setSetting('setBlocksBeyondCurrent',  MIN_BLOCKSBEYOND,    MAX_BLOCKSBEYOND,    false),
        onChangeTimeoutForFirstMatch: setSetting('setTimeoutForFirstMatch', MIN_TIMEOUT1STMATCH, MAX_TIMEOUT1STMATCH, false),

        onBlurConfidenceThreshold:  setSetting('setConfidenceThreshold',  MIN_CONFIDENCE,      MAX_CONFIDENCE,      true),
        onBlurBlockSizeMinutes:     setSetting('setBlockSizeMinutes',     MIN_BLOCKSIZE,       MAX_BLOCKSIZE,       true),
        onBlurBlocksBeyondCurrent:  setSetting('setBlocksBeyondCurrent',  MIN_BLOCKSBEYOND,    MAX_BLOCKSBEYOND,    true),
        onBlurTimeoutForFirstMatch: setSetting('setTimeoutForFirstMatch', MIN_TIMEOUT1STMATCH, MAX_TIMEOUT1STMATCH, true),
    }),
)(RawFaceRecognitionSection);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/faceRec/FaceRecognitionSection.tsx